import { useContext, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { XCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { DeterminationType, DeterminationSchema } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function Determination() {
	const financialContext = useContext(FinancialStatusContext);

	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		clearErrors,
		formState: { errors },
	} = useForm<DeterminationType>({
		resolver: zodResolver(DeterminationSchema),
		defaultValues: {
			maritalStatus: 'Single',
			hasDependents: false,
			dependents: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'dependents',
	});

	const hasDependents = watch('hasDependents');

	const onSubmit = (data: DeterminationType) => {
		financialContext.updateDetermination(data);
		financialContext.updateCurrentStep(4);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	const addDependent = () => {
		append({ age: null, relationship: '' });
	};

	useEffect(() => {
		if (!hasDependents) {
			setValue('dependents', []);
			clearErrors('dependents');
		}
	}, [hasDependents]);

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={3} />
				<div className="min-h-24">
					<Label currentStep="determination" value="maritalStatus" />
					<div className="flex space-x-3">
						{financialContext.jsonData.determination.radioQuestions?.[0]?.options.map((option) => (
							<div className="space-x-2" key={option.en}>
								<input {...register('maritalStatus')} id={option.en} type="radio" value={option.en} />
								<label htmlFor={option.en}>{option.en}</label>
							</div>
						))}
						{errors.maritalStatus ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="determination" value="maritalStatus" />
							</div>
						) : null}
					</div>
				</div>

				<div className="min-h-24">
					<Label currentStep="determination" value="hasDependents" />
					<div className="flex items-center space-x-3">
						<input {...register('hasDependents')} id="hasDependents" type="checkbox" />
						<label htmlFor="hasDependents">Yes</label>
						{errors.hasDependents ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="determination" value="hasDependents" />
							</div>
						) : null}
					</div>
				</div>

				{hasDependents ? (
					<div className="min-h-24">
						{/* <Label currentStep="determination" value="dependents" /> */}
						{fields.map((field, index) => (
							<div className="mb-2 flex items-center space-x-3" key={field.id}>
								<div className="flex flex-col">
									<Label currentStep="determination" value="dependentAge" />
									<input
										className={`${styles.smallInput} ${errors.dependents?.[index]?.age ? styles.error : ''}`}
										min="0"
										placeholder={financialContext.getPlaceholder({ step: 'determination', value: 'dependentAge' })}
										type="number"
										{...register(`dependents.${index}.age` as const, { valueAsNumber: true })}
									/>
								</div>
								<div className="flex flex-col">
									<Label currentStep="determination" value="dependentRelationship" />
									<input
										className={`${styles.smallInput} ${errors.dependents?.[index]?.relationship ? styles.error : ''}`}
										placeholder={financialContext.getPlaceholder({
											step: 'determination',
											value: 'dependentRelationship',
										})}
										type="text"
										{...register(`dependents.${index}.relationship` as const)}
									/>
								</div>
								<div className="mt-3">
									<button onClick={() => remove(index)} type="button">
										<XCircleIcon className="mt-12 h-8 w-8 text-red-700 md:mt-5" />
									</button>
								</div>
							</div>
						))}
						<div>
							<button className="mt-2 flex items-center space-x-3" onClick={addDependent} type="button">
								<div>{financialContext.language === 'en' ? 'Add Dependent' : 'Agregar dependiente'}</div>
								<PlusCircleIcon className="h-8 w-8 text-green-800" />
							</button>
						</div>
						<div>
							{errors.dependents ? (
								<div>
									<span className="text-balance text-xs">
										You must add at least one dependent or uncheck the checkbox to move on.
									</span>
								</div>
							) : null}
						</div>
					</div>
				) : (
					<div className="min-h-24"> </div>
				)}

				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(2);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
